import React from 'react';
import { Search, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import type { LocationFilters } from '../../types';

interface LocationsFiltersProps {
  filters: LocationFilters;
  onFiltersChange: (filters: Partial<LocationFilters>) => void;
  onClearFilters: () => void;
}

export const LocationsFilters: React.FC<LocationsFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
}) => {
  const hasActiveFilters = () => {
    return (
      (filters.searchQuery && filters.searchQuery.length > 0) ||
      (filters.estado && filters.estado !== 'TODOS') ||
      (filters.tipoLocal && filters.tipoLocal !== 'TODOS')
    );
  };

  const statusOptions = [
    { value: 'TODOS', label: 'Todos los estados' },
    { value: 'ACTIVO', label: 'Activo' },
    { value: 'INACTIVO', label: 'Inactivo' }
  ];

  const typeOptions = [
    { value: 'TODOS', label: 'Todos los tipos' },
    { value: 'PRINCIPAL', label: 'Principal' },
    { value: 'SUCURSAL', label: 'Sucursal' }
  ];


  const clearFilters = () => {
    onClearFilters();
  };

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Búsqueda */}
        <div className="lg:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Buscar sucursales..."
              value={filters.searchQuery || ''}
              onChange={(e) => onFiltersChange({ searchQuery: e.target.value })}
              className="pl-10"
            />
          </div>
        </div>

        {/* Estado */}
        <div>
          <Select
            value={filters.estado || 'TODOS'}
            onValueChange={(value) => onFiltersChange({ estado: value as LocationFilters['estado'] })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tipo */}
        <div>
          <Select
            value={filters.tipoLocal || 'TODOS'}
            onValueChange={(value) => onFiltersChange({ tipoLocal: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              {typeOptions.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters() && (
        <div className="flex justify-between items-center mt-4 pt-4 border-t">
          <div className="text-sm text-gray-600">
            Mostrando resultados filtrados
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-gray-600 hover:text-gray-900"
          >
            <Filter className="w-4 h-4 mr-2" />
            Limpiar filtros
          </Button>
        </div>
      )}

      {!hasActiveFilters() && (
        <div className="mt-4 pt-4 border-t">
          <div className="text-sm text-gray-600">
            Mostrando todas las sucursales
          </div>
        </div>
      )}
    </div>
  );
};